<template>
  <div class="journey-analytics-page">
    <analytics-header
      :date-from.sync="dateFrom"
      :date-to.sync="dateTo"
      :active-view.sync="activeView"
      :loading="loading"
      @search="handleSearch"
      @download="handleDownload"
    />

    <div v-if="loading && !hasActiveData" class="journey-analytics-page__loading">
      <div class="ja-loader">
        <div class="ja-loader__rings" aria-hidden="true">
          <span class="ja-loader__ring ja-loader__ring--outer" />
          <span class="ja-loader__ring ja-loader__ring--inner" />
          <span class="ja-loader__dot" />
        </div>
        <p class="ja-loader__title">Loading analytics</p>
        <p class="ja-loader__subtitle">{{ loaderSubtitle }}</p>
      </div>
    </div>

    <div v-else class="journey-analytics-page__content">
      <div
        v-if="loading"
        class="journey-analytics-page__refresh"
        aria-live="polite"
      >
        <div class="ja-loader ja-loader--compact">
          <div class="ja-loader__rings" aria-hidden="true">
            <span class="ja-loader__ring ja-loader__ring--outer" />
            <span class="ja-loader__ring ja-loader__ring--inner" />
            <span class="ja-loader__dot" />
          </div>
          <p class="ja-loader__title">{{ loaderRefreshTitle }}</p>
        </div>
      </div>

      <transition name="fade" mode="out-in">
        <funnel-view
          v-if="activeView === 'funnel'"
          key="funnel"
          :data="funnelData"
        />
        <activity-view
          v-else
          key="activity"
          :data="activityData"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import AnalyticsHeader from "./components/AnalyticsHeader.vue";
import FunnelView from "./components/FunnelView.vue";
import ActivityView from "./components/ActivityView.vue";
import { getDefaultDateRange } from "./mockData";
import {
  buildJourneyAnalyticsPayload,
  emptyActivityData,
  emptyFunnelData,
  fetchActivityAnalytics,
  fetchFunnelAnalytics,
  normalizeActivityData,
  normalizeFunnelData,
} from "@/services/journeyAnalytics";

export default {
  name: "JourneyAnalytics",
  components: {
    AnalyticsHeader,
    FunnelView,
    ActivityView,
  },
  data() {
    const range = getDefaultDateRange();

    return {
      dateFrom: range.from,
      dateTo: range.to,
      activeView: "funnel",
      loading: false,
      funnelData: emptyFunnelData(),
      activityData: emptyActivityData(),
      cache: {
        funnel: null,
        activity: null,
      },
      requestId: 0,
    };
  },
  computed: {
    hasActiveData() {
      return this.activeView === "funnel"
        ? !!this.cache.funnel
        : !!this.cache.activity;
    },
    loaderSubtitle() {
      return this.activeView === "funnel"
        ? "Fetching funnel data…"
        : "Fetching activity data…";
    },
    loaderRefreshTitle() {
      return this.activeView === "funnel"
        ? "Refreshing funnel…"
        : "Refreshing activity…";
    },
    filterKey() {
      return [
        this.formatDate(this.dateFrom),
        this.formatDate(this.dateTo),
      ].join("|");
    },
  },
  watch: {
    activeView(view) {
      this.loadView(view, { force: false });
    },
  },
  mounted() {
    this.loadView(this.activeView, { force: true });
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    getCached(view) {
      const entry = this.cache[view];
      if (!entry) return null;
      return entry.key === this.filterKey ? entry.data : null;
    },
    setCached(view, data) {
      this.cache = {
        ...this.cache,
        [view]: {
          key: this.filterKey,
          data,
        },
      };
    },
    clearCache() {
      this.cache = {
        funnel: null,
        activity: null,
      };
    },
    async loadView(view, { force = false } = {}) {
      if (!force) {
        const cached = this.getCached(view);
        if (cached) {
          if (view === "funnel") this.funnelData = cached;
          else this.activityData = cached;
          return;
        }
      }

      const requestId = ++this.requestId;
      this.loading = true;

      const payload = buildJourneyAnalyticsPayload({
        dateFrom: this.formatDate(this.dateFrom),
        dateTo: this.formatDate(this.dateTo),
      });

      try {
        const response = view === "funnel"
          ? await fetchFunnelAnalytics(payload)
          : await fetchActivityAnalytics(payload);

        if (requestId !== this.requestId) return;

        if (view === "funnel") {
          this.funnelData = normalizeFunnelData(response);
          this.setCached("funnel", this.funnelData);
        } else {
          this.activityData = normalizeActivityData(response);
          this.setCached("activity", this.activityData);
        }
      } catch (error) {
        if (requestId !== this.requestId) return;

        const message =
          (error && error.response && error.response.data && error.response.data.message) ||
          (error && error.message) ||
          "Failed to load journey analytics";

        this.$vs.notify({
          title: "Error",
          text: message,
          color: "danger",
          position: "top-right",
        });
      } finally {
        if (requestId === this.requestId) {
          this.loading = false;
        }
      }
    },
    handleSearch() {
      this.clearCache();
      this.loadView(this.activeView, { force: true });
    },
    escapeCsvCell(value) {
      const cell = value == null ? "" : String(value);
      if (/[",\n\r]/.test(cell)) {
        return `"${cell.replace(/"/g, '""')}"`;
      }
      return cell;
    },
    handleDownload() {
      const view = this.activeView;
      const data = view === "funnel" ? this.funnelData : this.activityData;
      const rows = [];

      if (view === "funnel") {
        rows.push(["Metric", "Value"]);
        data.kpis.forEach((k) => rows.push([k.title, k.value]));
        rows.push([]);
        rows.push(["Funnel Stage", "Count", "Percent"]);
        data.funnel.forEach((s) => rows.push([s.name, s.value, `${s.percent}%`]));
      } else {
        rows.push(["Metric", "Value"]);
        data.kpis.forEach((k) => rows.push([k.title, k.value]));
      }

      const csv = rows
        .map((r) => r.map((cell) => this.escapeCsvCell(cell)).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `journey-analytics-${view}-${Date.now()}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      this.$vs.notify({
        title: "Downloaded",
        text: "Analytics CSV export started",
        color: "primary",
        position: "top-right",
      });
    },
  },
};
</script>

<style scoped lang="scss">
.journey-analytics-page {
  padding: 0.5rem 0 2rem;

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 420px;
    padding: 2rem 1rem;
  }

  &__content {
    position: relative;
  }

  &__refresh {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 6rem;
    background: rgba(255, 255, 255, 0.62);
    backdrop-filter: blur(2px);
    border-radius: 18px;
  }
}

.ja-loader {
  width: 100%;
  max-width: 520px;
  text-align: center;
  background: #fff;
  border: 1px solid #ececf2;
  border-radius: 20px;
  box-shadow: 0 8px 28px rgba(20, 21, 34, 0.06);
  padding: 2.25rem 1.75rem 1.75rem;

  &--compact {
    width: auto;
    max-width: none;
    padding: 1.25rem 1.5rem;
    box-shadow: 0 10px 30px rgba(20, 21, 34, 0.1);

    .ja-loader__rings {
      width: 42px;
      height: 42px;
      margin-bottom: 0.75rem;
    }

    .ja-loader__ring--outer {
      border-width: 3px;
    }

    .ja-loader__ring--inner {
      inset: 8px;
      border-width: 2px;
    }

    .ja-loader__dot {
      width: 7px;
      height: 7px;
    }

    .ja-loader__title {
      margin: 0;
      font-size: 0.875rem;
    }

    .ja-loader__subtitle {
      display: none;
    }
  }

  &__rings {
    position: relative;
    width: 64px;
    height: 64px;
    margin: 0 auto 1.15rem;
  }

  &__ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border-style: solid;
    border-color: transparent;

    &--outer {
      border-width: 3px;
      border-top-color: #6366f1;
      border-right-color: rgba(99, 102, 241, 0.25);
      animation: ja-spin 0.9s linear infinite;
    }

    &--inner {
      inset: 12px;
      border-width: 2px;
      border-bottom-color: #8b5cf6;
      border-left-color: rgba(139, 92, 246, 0.2);
      animation: ja-spin-reverse 1.15s linear infinite;
    }
  }

  &__dot {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 10px;
    height: 10px;
    margin: -5px 0 0 -5px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.12);
    animation: ja-pulse 1.2s ease-in-out infinite;
  }

  &__title {
    margin: 0 0 0.35rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: #16172b;
  }

  &__subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #9ca3af;
    line-height: 1.4;
  }
}

@keyframes ja-spin {
  to { transform: rotate(360deg); }
}

@keyframes ja-spin-reverse {
  to { transform: rotate(-360deg); }
}

@keyframes ja-pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(0.82);
    opacity: 0.7;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>

<style lang="scss">
@import url("https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@800&display=swap");
</style>
